'use client';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ICrypto } from '@/lib/types';
import { getCryptos } from '@/services/supabase/crypto.service';
import { formatAmount } from '@/lib/helpers';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Coins, RefreshCw } from 'lucide-react';
import Link from 'next/link';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveTable, ColumnDef } from '@/components/ui/responsive-table';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { ScrollReveal } from '@/components/animations/ScrollReveal';
import { useRouter } from 'next/navigation';

const Crypto = () => {
  const router = useRouter();
  const [cryptoItems, setCryptoItems] = useState<ICrypto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCryptos = async () => {
      try {
        const items = await getCryptos();
        setCryptoItems(items || []);
      } catch (error) {
        console.error('Failed to fetch crypto data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCryptos();
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <ScrollReveal>
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="icon" onClick={() => router.back()}>
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Return to previous page</span>
          </Button>
        </div>
      </ScrollReveal>

      <ScrollReveal delay={0.1}>
        <div className="bg-card text-foreground p-8 rounded-lg mb-8 shadow-lg border border-border">
          <h1 className="text-4xl font-bold mb-4">Browse Crypto assets</h1>
          <p className="text-lg text-muted-foreground mb-6">
            Find the perfect crypto asset to invest in
          </p>
        </div>
      </ScrollReveal>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : cryptoItems.length === 0 ? (
        <ScrollReveal delay={0.2}>
          <EmptyState
            icon={Coins}
            title="No Crypto assets found"
            description="We couldn't load the crypto assets at this time. Please check back later."
            actionLabel="Refresh Page"
            actionLink="/crypto"
          />
        </ScrollReveal>
      ) : (
        <ScrollReveal delay={0.2}>
          <Card className="border border-border">
            <CardHeader>
              <CardTitle>Crypto Investment Opportunities</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveTable
                data={cryptoItems}
                rowKey="id"
                columns={[
                  {
                    header: 'Symbol',
                    primary: true,
                    cell: (item) => <span className="font-bold">{item.symbol}</span>
                  },
                  {
                    header: 'Name',
                    accessorKey: 'name'
                  },
                  {
                    header: 'Price',
                    cell: (item) => `$${item.price?.toFixed(2) || 'N/A'}`
                  },
                  {
                    header: '24h Change',
                    cell: (item) => (
                      <div className="flex items-center gap-1">
                        {item.change24h && item.change24h > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : item.change24h && item.change24h < 0 ? (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        ) : (
                          <Minus className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className={
                          item.change24h && item.change24h > 0 ? 'text-green-600' :
                            item.change24h && item.change24h < 0 ? 'text-red-600' : 'text-muted-foreground'
                        }>
                          {item.change24h ? `${item.change24h > 0 ? '+' : ''}${item.change24h}%` : 'N/A'}
                        </span>
                      </div>
                    )
                  },
                  {
                    header: 'Expected ROI',
                    cell: (item) => (
                      <span className="text-green-600 font-medium">
                        +{item.expectedROI || 0}%
                      </span>
                    )
                  },
                  {
                    header: 'Risk Level',
                    cell: (item) => (
                      <Badge variant={
                        item.riskLevel === 'Low' ? 'secondary' :
                          item.riskLevel === 'Medium' ? 'default' : 'destructive'
                      }>
                        {item.riskLevel || 'N/A'}
                      </Badge>
                    )
                  },
                  {
                    header: 'Min. Invest',
                    cell: (item) => formatAmount(item.minInvestment || 0)
                  }
                ]}
                actions={(item) => (
                  <Button asChild size="sm">
                    <Link href={`/crypto/${item.id}`}>
                      Invest
                    </Link>
                  </Button>
                )}
              />
            </CardContent>
          </Card>
        </ScrollReveal>
      )}
    </div>
  );
};

export default Crypto;
